import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Page, EnTetePage, Pastille } from '../../components/pro/ui';
import { proFetch } from '../../utils/proApi';
import EstimatifClient, { aUneDemande, titreDemande, resumeDemande } from '../../components/pro/EstimatifClient';

const SOURCE_LABELS = { devis: 'Devis', contact: 'Contact', assistant: 'Assistant' };
const SOURCE_COLORS = {
  devis: 'bg-orange-100 text-orange-700',
  contact: 'bg-blue-100 text-blue-700',
  assistant: 'bg-purple-100 text-purple-700',
};

// Trois colonnes : ce qui arrive, ce qu'on chiffre, ce qu'on construit.
// Les cinq statuts réels ne changent pas — ils se lisent sur la carte, et le
// bouton « suivant » les parcourt un par un. Regrouper l'affichage sans
// regrouper les statuts évite de perdre la différence entre un devis parti
// et un devis signé.
const STAGES = [
  { id: 'entrant',  label: 'Estimatif en ligne', ton: 'neutre', color: 'border-slate-200', statuts: ['estimatif'] },
  { id: 'devisage', label: 'Devisage',           ton: 'cours',  color: 'border-slate-200', statuts: ['visite', 'devis_envoye', 'accepte'] },
  { id: 'travaux',  label: 'En travaux',         ton: 'fait',   color: 'border-slate-200', statuts: ['en_travaux'] },
];

// L'enchaînement réel, inchangé : le bouton avance d'un cran à la fois.
const SUITE = ['estimatif', 'visite', 'devis_envoye', 'accepte', 'en_travaux'];
const STATUT_LABELS = {
  estimatif: 'Estimatif', visite: 'En visite', devis_envoye: 'Devis envoyé',
  accepte: 'Devis accepté', en_travaux: 'En travaux',
};

// Clos = sorti du pipeline actif (masqué par défaut)
const CLOSED = { id: 'clos', label: 'Clos', ton: 'neutre', color: 'border-slate-200', statuts: ['refuse', 'termine'] };
const CLOSED_LABELS = { refuse: 'Refusé', termine: 'Terminé' };

// Statut brut → statut canonique ('nouveau' est un synonyme d'entrée)
function statutDe(raw) {
  const s = raw || 'estimatif';
  return s === 'nouveau' ? 'estimatif' : s;
}

// Statut brut → colonne affichée ('clos' pour refuse/termine)
function mapStatus(raw) {
  const s = statutDe(raw);
  if (s === 'refuse' || s === 'termine') return 'clos';
  return STAGES.find(st => st.statuts.includes(s))?.id || 'entrant';
}

function timeAgo(date) {
  const d = new Date(date);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function ProDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('kanban'); // 'kanban' or 'list'
  const [showClosed, setShowClosed] = useState(false);
  const [menuFor, setMenuFor] = useState(null);      // ref du lead dont le menu ⋯ est ouvert
  const [confirmDelFor, setConfirmDelFor] = useState(null); // ref en attente de 2e tap Supprimer
  const [demandeDe, setDemandeDe] = useState(null);  // lead dont on regarde l'estimatif en ligne
  const plateauRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      params.set('limit', '100');
      const res = await proFetch(`/leads?${params}`);
      if (!res.ok) throw new Error('Erreur serveur');
      setLeads(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchLeads, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchLeads, search]);

  // Move lead to a new status
  const moveLead = async (ref, newStatus) => {
    try {
      const res = await proFetch(`/leads/${ref}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Erreur');
      setLeads(prev => prev.map(l =>
        l.ref === ref ? { ...l, status: newStatus, updatedAt: new Date().toISOString() } : l
      ));
    } catch (err) {
      console.error(err);
    }
  };

  // Fermer un lead (refusé / terminé) ou le supprimer définitivement (doublon)
  const closeLead = async (ref, status) => {
    setMenuFor(null);
    await moveLead(ref, status);
  };

  const deleteLead = async (ref) => {
    if (confirmDelFor !== ref) { setConfirmDelFor(ref); setTimeout(() => setConfirmDelFor(c => (c === ref ? null : c)), 3500); return; }
    setConfirmDelFor(null);
    setMenuFor(null);
    try {
      const res = await proFetch(`/leads/${ref}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erreur suppression');
      setLeads(prev => prev.filter(l => l.ref !== ref));
    } catch (err) {
      console.error(err);
    }
  };

  // Menu ⋯ par carte : Refusé / Terminé / Supprimer (2 taps)
  const renderLeadMenu = (lead) => (
    <div className="relative shrink-0">
      <button onClick={(e) => { e.preventDefault(); setMenuFor(m => (m === lead.ref ? null : lead.ref)); setConfirmDelFor(null); }}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 text-lg font-black"
        aria-label="Actions">⋯</button>
      {menuFor === lead.ref && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => { setMenuFor(null); setConfirmDelFor(null); }} />
          <div className="absolute right-0 top-9 z-20 w-44 bg-white rounded-xl shadow-xl border border-slate-100 p-1">
            {mapStatus(lead.status) !== 'clos' && (
              <>
                <button onClick={() => closeLead(lead.ref, 'refuse')}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 min-h-[40px]">❌ Marquer refusé</button>
                <button onClick={() => closeLead(lead.ref, 'termine')}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 min-h-[40px]">Marquer terminé</button>
              </>
            )}
            {mapStatus(lead.status) === 'clos' && (
              <button onClick={() => closeLead(lead.ref, 'estimatif')}
                className="w-full text-left px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 min-h-[40px]">↩️ Rouvrir (estimatif)</button>
            )}
            <button onClick={() => deleteLead(lead.ref)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold min-h-[40px] ${
                confirmDelFor === lead.ref ? 'bg-red-600 text-white' : 'text-red-600 hover:bg-red-50'
              }`}>
              {confirmDelFor === lead.ref ? 'Confirmer la suppression ?' : 'Supprimer'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  useEffect(() => {
    const ajuster = () => {
      const el = plateauRef.current;
      if (!el) return;
      const haut = el.getBoundingClientRect().top + window.scrollY;
      el.style.height = `${Math.max(320, window.innerHeight - haut)}px`;
      // Deuxième passe : ce qui dépasse encore, c'est la marge basse de la
      // page, qui n'est pas la même sur téléphone et sur bureau. On la retire
      // plutôt que de la coder en dur.
      const trop = document.documentElement.scrollHeight - window.innerHeight;
      if (trop > 0) el.style.height = `${Math.max(320, el.getBoundingClientRect().height - trop)}px`;
    };
    ajuster();
    window.addEventListener('resize', ajuster);
    return () => window.removeEventListener('resize', ajuster);
  });

  // Group leads by status for kanban
  const columns = STAGES.map(stage => ({
    ...stage,
    leads: leads.filter(l => mapStatus(l.status) === stage.id),
  }));
  const closedLeads = leads.filter(l => mapStatus(l.status) === 'clos');

  const visibleColumns = showClosed ? [...columns, { ...CLOSED, leads: closedLeads }] : columns;

  return (
    <Page large>
      <EnTetePage titre="Pipeline">
        <>
          {/* View toggle */}
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('kanban')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              Kanban
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              Liste
            </button>
          </div>
          {/* Afficher/masquer les leads clos (refusés + terminés) */}
          <button
            onClick={() => setShowClosed(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              showClosed ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}
          >
            Clos {leads.filter(l => mapStatus(l.status) === 'clos').length > 0 && `(${leads.filter(l => mapStatus(l.status) === 'clos').length})`}
          </button>
          <Link
            to="/pro/deviseur"
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-5 py-3 rounded-xl text-sm transition-colors"
          >
            + Nouveau devis
          </Link>
        </>
      </EnTetePage>

      {/* Search */}
      <div className="mb-5">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (nom, email, ref...)"
          className="w-full max-w-md px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:border-orange-500 outline-none"
        />
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-400">Chargement...</div>
      ) : view === 'kanban' ? (
        /* ── KANBAN VIEW ──
           Sur grand écran les colonnes tiennent dans la largeur, quelle que
           soit leur nombre. En dessous, les comprimer rendrait les noms
           illisibles (« Pierr… ») : largeur plancher et défilement. */
        <div ref={plateauRef} className="grid gap-3 overflow-x-auto kanban"
             style={{ '--colonnes': visibleColumns.length }}>
          {visibleColumns.map(col => (
            <div key={col.id} className="min-w-0 min-h-0 flex flex-col rounded-xl bg-slate-50/70 border border-slate-200 p-2.5">
              <div className="shrink-0 flex items-center gap-2 mb-2.5 px-1">
                <Pastille ton={col.ton || 'neutre'} />
                <span className="text-[13px] font-semibold text-slate-700">{col.label}</span>
                <span className="ml-auto text-xs text-slate-400 tabular-nums">{col.leads.length}</span>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-0.5">
                {col.leads.map(lead => (
                  <div key={lead._id} className="relative bg-white rounded-lg p-3 border border-slate-200">
                    <div className="absolute top-1.5 right-1.5">{renderLeadMenu(lead)}</div>
                    <Link to={`/pro/devis/${lead.ref}`} className="block pr-8">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm text-slate-900 truncate">
                          {[lead.client?.prenom, lead.client?.nom].filter(Boolean).join(' ') || 'Anonyme'}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${SOURCE_COLORS[lead.source]}`}>
                          {SOURCE_LABELS[lead.source] || lead.source}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{lead.ref} — {timeAgo(lead.createdAt)}</div>
                      {lead.client?.telephone && (
                        <div className="text-xs text-slate-500 mt-0.5">{lead.client.telephone}</div>
                      )}
                      {lead.context?.total && (
                        <div className="text-sm font-bold text-orange-600 mt-1">{lead.context.total}</div>
                      )}
                      {col.id === 'clos' && (
                        <div className="text-xs font-bold text-slate-500 mt-1">{CLOSED_LABELS[lead.status] || lead.status}</div>
                      )}
                      {col.statuts?.length > 1 && (
                        <div className="text-xs font-semibold text-slate-500 mt-1">{STATUT_LABELS[statutDe(lead.status)]}</div>
                      )}
                    </Link>

                    {aUneDemande(lead) && (
                      <button onClick={() => setDemandeDe(lead)}
                        className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold border border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 text-slate-600 rounded-lg transition-colors">
                        <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="2.6" />
                        </svg>
                        Sa demande ({resumeDemande(lead)})
                      </button>
                    )}

                    {/* Next stage button (forward only) */}
                    {(() => {
                      if (col.id === 'clos') return null;
                      const suivant = SUITE[SUITE.indexOf(statutDe(lead.status)) + 1];
                      if (!suivant) return null;

                      // "Estimatif créé" → just navigate to devis (status advances
                      // automatically in ProDevis when ≥1 room with ≥1 line exists)
                      if (statutDe(lead.status) === 'estimatif') {
                        return (
                          <div className="mt-2 pt-2 border-t border-slate-100">
                            <Link
                              to={`/pro/devis/${lead.ref}`}
                              className="w-full flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-700 transition-colors"
                            >
                              Commencer la visite &rarr;
                            </Link>
                          </div>
                        );
                      }

                      return (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <button
                            onClick={() => moveLead(lead.ref, suivant)}
                            className="w-full flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors active:bg-slate-200"
                          >
                            {STATUT_LABELS[suivant]} &rarr;
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                ))}
                {col.leads.length === 0 && (
                  <div className="text-center py-8 text-xs text-slate-400">Aucun lead</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── LIST VIEW ── */
        <div className="grid gap-3">
          {leads.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <p className="text-lg font-semibold">Aucun lead trouvé</p>
            </div>
          ) : leads.filter(l => showClosed || mapStatus(l.status) !== 'clos').map((lead) => {
            const rawStatus = lead.status || 'estimatif';
            const isClosed = mapStatus(rawStatus) === 'clos';
            const statut = statutDe(rawStatus);
            const libelle = isClosed ? (CLOSED_LABELS[rawStatus] || 'Clos') : STATUT_LABELS[statut];
            const suivant = isClosed ? null : SUITE[SUITE.indexOf(statut) + 1];
            return (
              <div key={lead._id} className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-slate-100">
                <Link to={`/pro/devis/${lead.ref}`} className="flex-1 flex items-center gap-4 min-w-0">
                  {/* Avatar */}
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-sm font-bold text-slate-600 shrink-0">
                    {(lead.client?.prenom?.[0] || lead.client?.nom?.[0] || '?').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 truncate text-sm">
                        {[lead.client?.prenom, lead.client?.nom].filter(Boolean).join(' ') || 'Anonyme'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${SOURCE_COLORS[lead.source]}`}>
                        {SOURCE_LABELS[lead.source] || lead.source}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {lead.ref} — {lead.client?.email || lead.client?.telephone || '—'}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-slate-400">{timeAgo(lead.createdAt)}</div>
                    {lead.context?.total && (
                      <div className="text-sm font-bold text-orange-600">{lead.context.total}</div>
                    )}
                  </div>
                </Link>
                {/* Status: current + next step button + menu ⋯ */}
                <div className="flex items-center gap-2 shrink-0">
                  {aUneDemande(lead) && (
                    <button onClick={() => setDemandeDe(lead)}
                      className="hidden sm:block px-3 py-2 rounded-lg text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 transition-colors">
                      Sa demande
                    </button>
                  )}
                  <span className="px-3 py-2 rounded-lg text-xs font-bold border-2 border-slate-200 bg-white">
                    {libelle}
                  </span>
                  {suivant && (
                    <button
                      onClick={() => moveLead(lead.ref, suivant)}
                      className="px-3 py-2 rounded-lg text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                      title={STATUT_LABELS[suivant]}
                    >
                      &rarr;
                    </button>
                  )}
                  {renderLeadMenu(lead)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Ce que le client a demandé en ligne ── */}
      {demandeDe && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4"
             onClick={() => setDemandeDe(null)}>
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[88vh] flex flex-col"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100 shrink-0">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-slate-900 truncate">
                  {[demandeDe.client?.prenom, demandeDe.client?.nom].filter(Boolean).join(' ') || 'Anonyme'}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {demandeDe.ref} · {titreDemande(demandeDe).toLowerCase()} le{' '}
                  {new Date(demandeDe.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <button onClick={() => setDemandeDe(null)}
                      className="shrink-0 p-1.5 -m-1 text-slate-400 hover:text-slate-600" aria-label="Fermer">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              <EstimatifClient lead={demandeDe} />
            </div>
            <div className="shrink-0 p-5 pt-4 border-t border-slate-100 flex justify-end">
              <Link to={`/pro/devis/${demandeDe.ref}`}
                    className="px-5 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl text-sm transition-colors">
                Ouvrir le relevé &rarr;
              </Link>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
